/**
 * buildCanvasGraph — builds CanvasGraph for the MAPPING CANVAS (field-level detail).
 *
 * ┌──────────────────────────────────────────────────────────────────────┐
 * │  This is the graph builder for the Mapping Canvas (GraphCanvas).    │
 * │  It converts MappingResponse → CanvasGraph at FIELD-LEVEL           │
 * │  granularity, with source/target fields, edges, joins, etc.        │
 * │                                                                     │
 * │  The pipeline canvas graph builder is pipelineNodeBuilder.ts in    │
 * │  ui/src/components/workspace/utils/ — a completely separate system. │
 * │                                                                     │
 * │  See docs/CANVAS_ARCHITECTURE.md for the full distinction.          │
 * └──────────────────────────────────────────────────────────────────────┘
 */
import type { MappingResponse, SchemaSetSnapshot } from '../../services/api';
import type { CanvasEdge, CanvasGraph, CanvasNode } from '../types';
import { normalizeTableKey } from '../utils/normalizeTableKey';
import { tablesMatch } from '../utils/tableMatching';
import {
  CANVAS_GROUP_BODY_PADDING,
  CANVAS_GROUP_HEADER_HEIGHT,
  CANVAS_GROUP_VERTICAL_GAP,
  CANVAS_GROUP_WIDTH,
  CANVAS_JOIN_BADGE_ROW_HEIGHT,
  CANVAS_ROW_SPACING,
  CANVAS_SOURCE_GROUP_X,
} from '../layoutConstants';
import { normaliseQualifiedValue, parseQualifiedName } from './qualifiedNames';
import { createBaseNode, ensureFieldNode } from './nodeFactory';
import { collectGroupLayouts } from './groupLayoutCollector';
import { buildGraphForMapping } from './mappingGraphBuilder';
import type { CanvasProjectSource, DetailTableFilter, NodeRegistry } from './types';

export type { CanvasProjectSource } from './types';

export const buildCanvasGraph = (
  response?: MappingResponse | null,
  showGrid: boolean = true,
  layoutDensity: string = 'comfortable',
  expandedGroups?: Set<string>,
  focusedTargetIds?: Set<string>,
  focusedSourceIds?: Set<string>,
  projectSources?: CanvasProjectSource[],
  activeSourceTables?: Set<string>,
  activeTargetTables?: Set<string>,
  /** When set, show only the drilled table (+ connected tables) and hide project-source stack. */
  detailFilter?: DetailTableFilter | null,
): CanvasGraph => {
  if (!response) {
    return { nodes: [], edges: [] };
  }

  // Phase 3: Focus Mode Filtering
  const hasFocusTargets = focusedTargetIds && focusedTargetIds.size > 0;
  const hasFocusSources = focusedSourceIds && focusedSourceIds.size > 0;

  const rawMappings = Array.isArray(response.mappings) ? response.mappings : [];
  let effectiveMappings = rawMappings.filter(m => {
    if (!hasFocusTargets && !hasFocusSources) return true;

    let keep = true;
    if (hasFocusTargets) {
      const target = (m.target_table ?? 'target').trim() || 'target';
      keep = keep && focusedTargetIds!.has(target);
    }
    if (hasFocusSources) {
      const source = (m.source_file ?? 'source').trim() || 'source';
      const sourceNK = normalizeTableKey(source);
      keep = keep && (focusedSourceIds!.has(source) ||
        Array.from(focusedSourceIds!).some(id => normalizeTableKey(id) === sourceNK));
    }
    return keep;
  });

  if (detailFilter) {
    effectiveMappings = effectiveMappings.filter((m) => {
      if (detailFilter.role === 'source') {
        const source = (m.source_file ?? 'source').trim() || 'source';
        return tablesMatch(source, detailFilter.tableId);
      }
      const target = (m.target_table ?? 'target').trim() || 'target';
      return tablesMatch(target, detailFilter.tableId);
    });
  }

  const filteredResponse = {
    ...response,
    mappings: effectiveMappings
  };

  const nodes: CanvasNode[] = [];
  const edges: CanvasEdge[] = [];
  const registry: NodeRegistry = {
    source: new Map(),
    target: new Map(),
    intermediate: new Map(),
    bridge: new Map(),
    usedOffsets: new Set(),
  };

  const layout = collectGroupLayouts(
    filteredResponse,
    expandedGroups,
    hasFocusTargets || hasFocusSources || !!detailFilter,
    detailFilter,
  );

  if (detailFilter?.role === 'source') {
    for (const alias of Array.from(layout.sourceGroups.keys())) {
      if (!tablesMatch(alias, detailFilter.tableId)) {
        layout.sourceGroups.delete(alias);
      }
    }
  } else if (detailFilter?.role === 'target') {
    for (const table of Array.from(layout.targetGroups.keys())) {
      if (!tablesMatch(table, detailFilter.tableId)) {
        layout.targetGroups.delete(table);
      }
    }
  }

  // Phase 4a: Create origin group container nodes (only when multiple origins exist)
  const collapsedOrigins = new Set<string>();
  if (layout.originGroups.size > 0) {
    layout.originGroups.forEach((og) => {
      if (!og.expanded) collapsedOrigins.add(og.id);
      nodes.push(
        createBaseNode(
          og.id,
          'sourceOriginGroup',
          { x: og.x, y: og.y },
          {
            label: og.label,
            qualifiedName: og.id,
            table: '',
            field: '',
            mappingIds: [],
            originLabel: og.label,
            originType: og.originType,
            childTableCount: og.childTableCount,
            childMappedFields: og.childMappedFields,
            childTotalFields: og.childTotalFields,
            originExpanded: og.expanded,
            originConnectorId: og.originType === 'connector' ? og.id.replace('origin:', '') : undefined,
          },
          {
            style: {
              width: og.width,
              height: og.height,
              pointerEvents: 'none',
              zIndex: -1,
            },
          }
        )
      );
    });
  }

  // Phase 0d: Determine which groups should be dimmed based on active table selection.
  const hasActiveSourceFilter = activeSourceTables && activeSourceTables.size > 0;
  const hasActiveTargetFilter = activeTargetTables && activeTargetTables.size > 0;

  // Create group nodes first so children can attach
  layout.sourceGroups.forEach((group) => {
    // Skip child table groups when their origin container is collapsed
    if (group.originGroupKey && collapsedOrigins.has(`origin:${group.originGroupKey}`)) {
      return;
    }
    const isDimmed = hasActiveSourceFilter
      ? !activeSourceTables!.has(group.originalLabel) && !activeSourceTables!.has(group.originalLabel.toLowerCase())
      : false;
    nodes.push(
      createBaseNode(
        group.id,
        'tableGroup',
        { x: group.x, y: group.y },
        {
          label: group.label,
          qualifiedName: group.originalLabel,
          table: group.originalLabel,
          field: '',
          mappingIds: [],
          groupRole: group.role,
          groupExpanded: group.expanded ?? false,
          groupMappedCount: group.mappedCount ?? 0,
          groupTotalFieldCount: group.totalFieldCount ?? 0,
          originGroupId: group.originGroupKey ? `origin:${group.originGroupKey}` : undefined,
          dimmed: isDimmed,
        },
        {
          style: {
            width: CANVAS_GROUP_WIDTH,
            height: group.height,
            pointerEvents: 'none',
            zIndex: 0,
          },
        }
      )
    );
  });

  // Track the bottom of all source content for stacking unmapped sources + "Add Source" node
  let sourceStackBottomY = 40;
  layout.sourceGroups.forEach((g) => {
    const bottom = g.y + g.height + CANVAS_GROUP_VERTICAL_GAP;
    if (bottom > sourceStackBottomY) sourceStackBottomY = bottom;
  });
  layout.originGroups.forEach((og) => {
    const bottom = og.y + og.height + CANVAS_GROUP_VERTICAL_GAP;
    if (bottom > sourceStackBottomY) sourceStackBottomY = bottom;
  });

  // W1: Add unmapped project sources as empty dashed table groups
  if (
    projectSources &&
    projectSources.length > 0 &&
    !hasFocusTargets &&
    !hasFocusSources &&
    !detailFilter
  ) {
    const existingAliases = new Set(Array.from(layout.sourceGroups.keys()).map(a => a.toLowerCase()));

    for (const ps of projectSources) {
      if (ps.status === 'removed') continue;
      const label = ps.label ?? ps.source_id.slice(0, 8);
      if (existingAliases.has(label.toLowerCase())) continue;
      const statusChip = ps.status === 'profiled' ? 'Pending mapping' : ps.status;
      const groupId = `group:source:unmapped:${ps.source_id}`;
      const height = CANVAS_GROUP_HEADER_HEIGHT + 16;
      const originGroupId = ps.source_type === 'connector' && ps.connector_id
        ? `origin:${ps.connector_id}`
        : undefined;
      nodes.push(
        createBaseNode(
          groupId,
          'tableGroup',
          { x: CANVAS_SOURCE_GROUP_X, y: sourceStackBottomY },
          {
            label: `${label}`,
            qualifiedName: label,
            table: label,
            field: '',
            mappingIds: [],
            groupRole: 'source',
            groupExpanded: false,
            groupMappedCount: 0,
            groupTotalFieldCount: 0,
            status: statusChip,
            originGroupId,
          },
          {
            style: {
              width: CANVAS_GROUP_WIDTH,
              height,
              pointerEvents: 'none',
              zIndex: 0,
              border: '2px dashed rgba(37, 99, 235, 0.3)',
              opacity: 0.7,
            },
          }
        )
      );
      sourceStackBottomY += height + CANVAS_GROUP_VERTICAL_GAP;
    }
  }

  // Persistent "Add Source" action node at bottom of source stack (hidden during focus / drill-down)
  if (projectSources && !hasFocusTargets && !hasFocusSources && !detailFilter) {
    const addSourceNodeId = '__add_source__';
    nodes.push(
      createBaseNode(
        addSourceNodeId,
        'tableGroup',
        { x: CANVAS_SOURCE_GROUP_X, y: sourceStackBottomY },
        {
          label: '\u2795 Add Source',
          qualifiedName: addSourceNodeId,
          table: '',
          field: '',
          mappingIds: [],
          isAddSourceAction: true,
          groupExpanded: false,
          groupMappedCount: 0,
          groupTotalFieldCount: 0,
        },
        {
          style: {
            width: CANVAS_GROUP_WIDTH,
            height: 44,
            border: '2px dashed rgba(124, 58, 237, 0.3)',
            opacity: 0.8,
            cursor: 'pointer',
          },
        },
      ),
    );
  }

  layout.targetGroups.forEach((group) => {
    const isDimmed = hasActiveTargetFilter
      ? !activeTargetTables!.has(group.originalLabel) && !activeTargetTables!.has(group.originalLabel.toLowerCase())
      : false;
    nodes.push(
      createBaseNode(
        group.id,
        'tableGroup',
        { x: group.x, y: group.y },
        {
          label: group.label,
          qualifiedName: group.originalLabel,
          table: group.originalLabel,
          field: '',
          mappingIds: [],
          groupRole: group.role,
          groupExpanded: group.expanded ?? false,
          groupMappedCount: group.mappedCount ?? 0,
          groupTotalFieldCount: group.totalFieldCount ?? 0,
          dimmed: isDimmed,
        },
        {
          style: {
            width: CANVAS_GROUP_WIDTH,
            height: group.height,
            pointerEvents: 'none',
            zIndex: 0,
          },
        }
      )
    );
  });

  const sortedMappings = [...effectiveMappings].sort((a, b) => {
    const left = `${a.target_table}.${a.target_field}`.toLowerCase();
    const right = `${b.target_table}.${b.target_field}`.toLowerCase();
    if (left === right) {
      return a.source_field.localeCompare(b.source_field);
    }
    return left.localeCompare(right);
  });

  const groupNextIndex = new Map<string, number>();
  const getNextIndex = (groupId: string): number => {
    if (!groupNextIndex.has(groupId)) {
      const group = Array.from(layout.sourceGroups.values()).find(g => g.id === groupId)
        ?? Array.from(layout.targetGroups.values()).find(g => g.id === groupId);
      if (group) {
        const count = Math.round((group.height - CANVAS_GROUP_HEADER_HEIGHT - CANVAS_GROUP_BODY_PADDING * 2) / CANVAS_ROW_SPACING);
        groupNextIndex.set(groupId, count);
      } else {
        groupNextIndex.set(groupId, 0);
      }
    }
    const current = groupNextIndex.get(groupId)!;
    groupNextIndex.set(groupId, current + 1);
    return current;
  };

  // Determine the source schema snapshot
  const meta2 = response.metadata;
  let sourceSnapshot: SchemaSetSnapshot | undefined = meta2?.source_schema_set_snapshot;
  if (!sourceSnapshot && meta2?.source_schema_signature) {
    const signature = meta2.source_schema_signature;
    if (signature.sources && Array.isArray(signature.sources)) {
      sourceSnapshot = {
        name: 'source-signature',
        status: 'active',
        tables: signature.sources.reduce<Record<string, { name: string; display_name: string; columns: typeof signature.sources[0]['columns'] }>>((acc, src) => {
          acc[src.source] = {
            name: src.source,
            display_name: src.source,
            columns: src.columns,
          };
          return acc;
        }, {}),
      };
    }
  }

  const sourceAliasMap = meta2?.source_alias_map;

  const targetSnapshot = response.transformation_plan?.target_schema_snapshot
    ?? meta2?.target_schema_snapshot
    ?? meta2?.target_schema_snapshot
    ?? meta2?.schema_context?.target_schema_snapshot;
  const targetAliasMap = meta2?.schema_alias_map?.tables;

  sortedMappings.forEach((mapping) => {
    buildGraphForMapping(mapping, registry, nodes, edges, layout, getNextIndex, sourceSnapshot, sourceAliasMap, targetSnapshot, targetAliasMap);
  });

  // Phase 1 (P0): Create bundle edges between collapsed groups.
  const bundleCounts = new Map<string, number>();
  sortedMappings.forEach((mapping) => {
    const sourceAlias = (mapping.source_file ?? 'source').trim() || 'source';
    const targetAlias = (mapping.target_table ?? 'target').trim() || 'target';
    const sourceGroup = layout.sourceGroups.get(sourceAlias);
    const targetGroup = layout.targetGroups.get(targetAlias);
    if (!sourceGroup || !targetGroup) return;

    const originCollapsed = sourceGroup.originGroupKey
      ? collapsedOrigins.has(`origin:${sourceGroup.originGroupKey}`)
      : false;
    const sourceCollapsed = !sourceGroup.expanded;
    const targetCollapsed = !targetGroup.expanded;

    if (originCollapsed || sourceCollapsed || targetCollapsed) {
      const sourceNodeId = originCollapsed
        ? `origin:${sourceGroup.originGroupKey}`
        : sourceGroup.id;
      const key = `${sourceNodeId}::${targetGroup.id}`;
      bundleCounts.set(key, (bundleCounts.get(key) ?? 0) + 1);
    }
  });
  bundleCounts.forEach((count, key) => {
    const [sourceGroupId, targetGroupId] = key.split('::');
    edges.push({
      id: `bundle:${key}`,
      source: sourceGroupId,
      target: targetGroupId,
      sourceHandle: 'source',
      targetHandle: 'target',
      type: 'bundle',
      data: {
        bundleMappingCount: count,
      },
    } as CanvasEdge);
  });

  // Phase 4: Fill in unmapped fields for Source Groups
  layout.sourceGroups.forEach((group) => {
    const alias = group.originalLabel;
    Array.from(layout.sourceFieldIndex).forEach((entry) => {
      const [qualified, index] = entry;
      if (!qualified.startsWith(alias + '.')) return;

      const canonical = normaliseQualifiedValue(qualified);
      if (registry.source.has(canonical)) return;

      ensureFieldNode(
        registry,
        nodes,
        qualified,
        'sourceField',
        group,
        index,
        undefined,
        false,
        undefined
      );

      const node = registry.source.get(canonical);
      if (node) {
        node.data.isUnmapped = true;
      }
    });
  });

  // Phase 4: Fill in unmapped fields for Target Groups
  layout.targetGroups.forEach((group) => {
    const table = group.originalLabel;
    Array.from(layout.targetFieldIndex).forEach((entry) => {
      const [qualified, index] = entry;
      if (!qualified.startsWith(table + '.')) return;

      const canonical = normaliseQualifiedValue(qualified);
      if (registry.target.has(canonical)) return;

      ensureFieldNode(
        registry,
        nodes,
        qualified,
        'targetField',
        group,
        index,
        undefined,
        false,
        undefined
      );

      const node = registry.target.get(canonical);
      if (node) {
        node.data.isUnmapped = true;
      }
    });
  });

  // Phase 5: Populate Join Badges on Source Group Headers
  if (Array.isArray(response.relationships) && response.relationships.length > 0) {
    response.relationships.forEach((rel) => {
      const leftFile = (rel.left_table ?? '').trim();
      const rightFile = (rel.right_table ?? '').trim();

      const leftGroupId = `group:source:${leftFile}`;
      const rightGroupId = `group:source:${rightFile}`;

      const leftNode = nodes.find(n => n.id === leftGroupId);
      const rightNode = nodes.find(n => n.id === rightGroupId);

      const badge = {
        joinType: rel.join_type ?? 'inner',
        confidence: rel.confidence ?? 0,
        joinKeys: rel.join_keys ?? [],
      };

      if (leftNode) {
        if (!leftNode.data.joinBadges) leftNode.data.joinBadges = [];
        leftNode.data.joinBadges.push({ ...badge, otherTable: rightFile });
      }
      if (rightNode) {
        if (!rightNode.data.joinBadges) rightNode.data.joinBadges = [];
        rightNode.data.joinBadges.push({ ...badge, otherTable: leftFile });
      }
    });

    // Adjust group heights and shift child field positions for groups with badges
    const groupsWithBadges = new Set<string>();
    nodes.forEach(n => {
      if (n.data.kind === 'tableGroup' && n.data.joinBadges && n.data.joinBadges.length > 0) {
        groupsWithBadges.add(n.id);
      }
    });

    if (groupsWithBadges.size > 0) {
      nodes.forEach(n => {
        if (groupsWithBadges.has(n.id) && n.style) {
          const currentHeight = typeof n.style.height === 'number' ? n.style.height : 0;
          if (currentHeight > 0) {
            n.style = { ...n.style, height: currentHeight + CANVAS_JOIN_BADGE_ROW_HEIGHT };
          }
        }
      });

      nodes.forEach(n => {
        if (n.parentNode && groupsWithBadges.has(n.parentNode)) {
          n.position = { ...n.position, y: n.position.y + CANVAS_JOIN_BADGE_ROW_HEIGHT };
        }
      });
    }
  }

  // Post-build validation: remove edges whose source or target node does not exist
  const nodeIdSet = new Set(nodes.map(n => n.id));
  const validEdges = edges.filter(edge => {
    const sourceExists = nodeIdSet.has(edge.source);
    const targetExists = nodeIdSet.has(edge.target);
    if (!sourceExists || !targetExists) {
      console.warn(
        `[Canvas] Removing dangling edge ${edge.id}: source=${edge.source} (${sourceExists ? 'ok' : 'MISSING'}), target=${edge.target} (${targetExists ? 'ok' : 'MISSING'})`
      );
      return false;
    }
    return true;
  });

  // Remove intermediate field nodes that have no incoming edges (orphan references)
  const nodesWithIncomingEdge = new Set(validEdges.map(e => e.target));
  const cleanedNodes = nodes.filter(n => {
    if (n.type === 'intermediateField' && !nodesWithIncomingEdge.has(n.id)) {
      console.warn(`[Canvas] Removing orphan intermediate node ${n.id}`);
      return false;
    }
    return true;
  });

  return { nodes: cleanedNodes, edges: validEdges };
};
